import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [period, setPeriod] = useState("30d");
  const [error, setError] = useState("");

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin");
        return false;
      }
      return true;
    } catch (error) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    setLoading(true);
    setError("");

    try {
      const [statsRes, revenueRes] = await Promise.all([
        axios.get(
          `${API_BASE_URL}/api/admin/dashboard/stats?period=${period}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
        axios.get(`${API_BASE_URL}/api/revenue/analytics?groupBy=day`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setStats(statsRes.data);
      setRevenueData(revenueRes.data);
    } catch (error) {
      setError("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchData();
    }
  }, [checkPortalAdmin, fetchData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  const StatCard = ({ title, value, subtitle, icon, trend, trendPositive }) => (
    <div
      style={{
        background: "white",
        border: "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <span style={{ fontSize: "14px", color: "#6b7280" }}>{title}</span>
        <span style={{ fontSize: "24px" }}>{icon}</span>
      </div>
      <div style={{ fontSize: "28px", fontWeight: "700", color: "#1a1a1a" }}>
        {value}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ fontSize: "13px", color: "#6b7280" }}>{subtitle}</span>
        {trend !== undefined && (
          <span
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: trendPositive ? "#10b981" : "#ef4444",
              display: "flex",
              alignItems: "center",
              gap: "2px",
            }}
          >
            {trendPositive ? "↑" : "↓"} {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <PortalAdminLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <div className="spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div
        style={{
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "24px",
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "24px" }}>
          <h1
            style={{ fontSize: "28px", fontWeight: "700", margin: "0 0 8px" }}
          >
            📊 Analytics Dashboard
          </h1>
          <p style={{ color: "#6b7280", margin: 0 }}>
            Platform-wide metrics and performance insights
          </p>
        </div>

        {error && (
          <div
            style={{
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "24px",
              background: "#fee2e2",
              border: "1px solid #ef4444",
              color: "#991b1b",
            }}
          >
            {error}
          </div>
        )}

        {/* Period Selector */}
        <div style={{ marginBottom: "24px", display: "flex", gap: "8px" }}>
          {["7d", "30d", "90d", "ytd"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border:
                  period === p ? "2px solid #3b82f6" : "1px solid #e5e7eb",
                background: period === p ? "#eff6ff" : "white",
                color: period === p ? "#1e40af" : "#6b7280",
                fontWeight: period === p ? "600" : "400",
                cursor: "pointer",
                fontSize: "14px",
              }}
            >
              {p === "7d"
                ? "Last 7 Days"
                : p === "30d"
                  ? "Last 30 Days"
                  : p === "90d"
                    ? "Last 90 Days"
                    : "Year to Date"}
            </button>
          ))}
        </div>

        {/* Stats Grid */}
        {stats && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "16px",
                marginBottom: "32px",
              }}
            >
              <StatCard
                title="Total Revenue"
                value={formatCurrency(stats.revenue?.totalThisPeriod)}
                subtitle="This period"
                icon="💰"
                trend={stats.revenue?.growth}
                trendPositive={stats.revenue?.growth > 0}
              />
              <StatCard
                title="Monthly Recurring Revenue"
                value={formatCurrency(stats.subscriptions?.monthlyRevenue)}
                subtitle="From active subscriptions"
                icon="💰"
                trend={stats.subscriptions?.growth}
                trendPositive={stats.subscriptions?.growth > 0}
              />
              <StatCard
                title="Total Users"
                value={formatNumber(stats.users?.total)}
                subtitle={`+${stats.users?.newThisPeriod} new`}
                icon="👥"
                trend={stats.users?.growth}
                trendPositive={stats.users?.growth > 0}
              />
              <StatCard
                title="Active Businesses"
                value={formatNumber(stats.businesses?.active)}
                subtitle={`${stats.businesses?.total} total`}
                icon="🏢"
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "16px",
                marginBottom: "32px",
              }}
            >
              <StatCard
                title="Cancellation Platform Income"
                value={formatCurrency(
                  stats.revenue?.cancellationPlatformIncome,
                )}
                subtitle={`${formatNumber(stats.revenue?.cancellationPlatformIncomeCount)} attributed cancellations`}
                icon="🧾"
                trend={stats.revenue?.cancellationPlatformIncomeGrowth}
                trendPositive={
                  (stats.revenue?.cancellationPlatformIncomeGrowth || 0) >= 0
                }
              />
              <StatCard
                title="Total Bookings"
                value={formatNumber(stats.bookings?.thisPeriod)}
                subtitle={`${stats.bookings?.completed} completed`}
                icon="📅"
                trend={stats.bookings?.growth}
                trendPositive={stats.bookings?.growth > 0}
              />
              <StatCard
                title="Total Orders"
                value={formatNumber(stats.orders?.thisPeriod)}
                subtitle={`${stats.orders?.total} all time`}
                icon="🛒"
                trend={stats.orders?.growth}
                trendPositive={stats.orders?.growth > 0}
              />
              <StatCard
                title="Active Subscriptions"
                value={formatNumber(stats.subscriptions?.activeCount)}
                subtitle={`${stats.shops?.subscribed} shops, ${stats.staff?.subscribedFreelancers} freelancers`}
                icon="✅"
              />
              <StatCard
                title="Active Trials"
                value={formatNumber(
                  (stats.shops?.trial || 0) +
                    (stats.staff?.trialFreelancers || 0),
                )}
                subtitle={`${stats.shops?.trial || 0} shops, ${stats.staff?.trialFreelancers || 0} freelancers`}
                icon="🔄"
              />
            </div>

            {/* Subscription Details */}
            <div
              style={{
                background: "white",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                padding: "24px",
                marginBottom: "24px",
              }}
            >
              <h2
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "8px",
                }}
              >
                📊 Subscription Breakdown
              </h2>
              <p
                style={{
                  fontSize: "14px",
                  color: "#6b7280",
                  marginBottom: "16px",
                }}
              >
                Detailed view of all subscription states across shops and
                freelancers
              </p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "24px",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    background: "#f0fdf4",
                    borderRadius: "8px",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: "700",
                      color: "#16a34a",
                    }}
                  >
                    {formatNumber(stats.shops?.subscribed)}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#15803d",
                      fontWeight: "500",
                    }}
                  >
                    Paying Shops
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "4px",
                    }}
                  >
                    Active subscriptions
                  </div>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    background: "#fef3c7",
                    borderRadius: "8px",
                    border: "1px solid #fde68a",
                  }}
                >
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: "700",
                      color: "#d97706",
                    }}
                  >
                    {formatNumber(stats.staff?.subscribedFreelancers)}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#b45309",
                      fontWeight: "500",
                    }}
                  >
                    Paying Freelancers
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "4px",
                    }}
                  >
                    Active subscriptions
                  </div>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    background: "#dbeafe",
                    borderRadius: "8px",
                    border: "1px solid #93c5fd",
                  }}
                >
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: "700",
                      color: "#2563eb",
                    }}
                  >
                    {formatNumber(
                      (stats.shops?.trial || 0) +
                        (stats.staff?.trialFreelancers || 0),
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#1e40af",
                      fontWeight: "500",
                    }}
                  >
                    On Trial Period
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "4px",
                    }}
                  >
                    {stats.shops?.trial || 0} shops,{" "}
                    {stats.staff?.trialFreelancers || 0} freelancers
                  </div>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    background: "#fee2e2",
                    borderRadius: "8px",
                    border: "1px solid #fecaca",
                  }}
                >
                  <div
                    style={{
                      fontSize: "32px",
                      fontWeight: "700",
                      color: "#dc2626",
                    }}
                  >
                    {formatNumber(
                      (stats.shops?.expired || 0) +
                        (stats.staff?.expiredFreelancers || 0),
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#b91c1c",
                      fontWeight: "500",
                    }}
                  >
                    Expired/Cancelled
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "4px",
                    }}
                  >
                    {stats.shops?.expired || 0} shops,{" "}
                    {stats.staff?.expiredFreelancers || 0} freelancers
                  </div>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    padding: "16px",
                    background: "#f3e8ff",
                    borderRadius: "8px",
                    border: "1px solid #d8b4fe",
                  }}
                >
                  <div
                    style={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#7c3aed",
                    }}
                  >
                    {formatCurrency(stats.subscriptions?.ytdRevenue)}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#6b21a8",
                      fontWeight: "500",
                    }}
                  >
                    YTD Subscription Revenue
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginTop: "4px",
                    }}
                  >
                    Earned in {new Date().getFullYear()}
                  </div>
                </div>
              </div>
            </div>

            {/* Revenue Breakdown */}
            {revenueData && (
              <div
                style={{
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "24px",
                  marginBottom: "24px",
                }}
              >
                <h2
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    marginBottom: "16px",
                  }}
                >
                  💵 Revenue Breakdown
                </h2>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "24px",
                  }}
                >
                  <div
                    style={{
                      padding: "20px",
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      borderRadius: "12px",
                      color: "white",
                    }}
                  >
                    <div style={{ fontSize: "14px", opacity: 0.9 }}>
                      Orders Revenue
                    </div>
                    <div
                      style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        marginTop: "8px",
                      }}
                    >
                      {formatCurrency(revenueData.byType?.orders?.sales)}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.8,
                        marginTop: "4px",
                      }}
                    >
                      {revenueData.byType?.orders?.count} orders
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "20px",
                      background:
                        "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                      borderRadius: "12px",
                      color: "white",
                    }}
                  >
                    <div style={{ fontSize: "14px", opacity: 0.9 }}>
                      Bookings Revenue
                    </div>
                    <div
                      style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        marginTop: "8px",
                      }}
                    >
                      {formatCurrency(revenueData.byType?.bookings?.sales)}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.8,
                        marginTop: "4px",
                      }}
                    >
                      {revenueData.byType?.bookings?.count} bookings
                    </div>
                  </div>
                  <div
                    style={{
                      padding: "20px",
                      background:
                        "linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)",
                      borderRadius: "12px",
                      color: "white",
                    }}
                  >
                    <div style={{ fontSize: "14px", opacity: 0.9 }}>
                      Packages Revenue
                    </div>
                    <div
                      style={{
                        fontSize: "28px",
                        fontWeight: "700",
                        marginTop: "8px",
                      }}
                    >
                      {formatCurrency(revenueData.byType?.packages?.sales)}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        opacity: 0.8,
                        marginTop: "4px",
                      }}
                    >
                      {revenueData.byType?.packages?.count} packages
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Daily Activity Chart (Simple table view) */}
            {stats.charts?.dailyBookings?.length > 0 && (
              <div
                style={{
                  background: "white",
                  border: "1px solid #e5e7eb",
                  borderRadius: "12px",
                  padding: "24px",
                  marginBottom: "24px",
                }}
              >
                <h2
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    marginBottom: "16px",
                  }}
                >
                  📅 Recent Daily Activity
                </h2>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "14px",
                    }}
                  >
                    <thead>
                      <tr style={{ borderBottom: "2px solid #e5e7eb" }}>
                        <th
                          style={{
                            textAlign: "left",
                            padding: "12px 8px",
                            color: "#6b7280",
                          }}
                        >
                          Date
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: "12px 8px",
                            color: "#6b7280",
                          }}
                        >
                          Bookings
                        </th>
                        <th
                          style={{
                            textAlign: "right",
                            padding: "12px 8px",
                            color: "#6b7280",
                          }}
                        >
                          Revenue
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.charts.dailyBookings
                        .slice(-10)
                        .reverse()
                        .map((day, idx) => (
                          <tr
                            key={idx}
                            style={{ borderBottom: "1px solid #f3f4f6" }}
                          >
                            <td style={{ padding: "12px 8px" }}>{day._id}</td>
                            <td
                              style={{
                                padding: "12px 8px",
                                textAlign: "right",
                              }}
                            >
                              {day.bookings}
                            </td>
                            <td
                              style={{
                                padding: "12px 8px",
                                textAlign: "right",
                                fontWeight: "600",
                                color: "#10b981",
                              }}
                            >
                              {formatCurrency(day.revenue)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Quick Actions */}
        <div
          style={{
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "24px",
          }}
        >
          <h2
            style={{
              fontSize: "18px",
              fontWeight: "600",
              marginBottom: "16px",
            }}
          >
            ⚡ Quick Actions
          </h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            <button
              onClick={() => navigate("/portal-admin/users")}
              style={{
                padding: "12px 24px",
                background: "#3b82f6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              👥 Manage Users
            </button>
            <button
              onClick={() => navigate("/portal-admin/shops")}
              style={{
                padding: "12px 24px",
                background: "#10b981",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              🏪 Manage Shops
            </button>
            <button
              onClick={() => navigate("/portal-admin/settings")}
              style={{
                padding: "12px 24px",
                background: "#6b7280",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              ⚙️ Platform Settings
            </button>
            <button
              onClick={() => fetchData()}
              style={{
                padding: "12px 24px",
                background: "white",
                color: "#3b82f6",
                border: "1px solid #3b82f6",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              🔄 Refresh Data
            </button>
          </div>
        </div>
      </div>
    </PortalAdminLayout>
  );
};

export default AnalyticsDashboard;
